import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { previewPages } from '../../data/content';

/**
 * Real Volume 2 pages in vertical cards. Native scroll-snap = natural swipe
 * on mobile; arrows + dots on desktop.
 */
export function PreviewCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const total = previewPages.length;
  const getStep = () => {
    const track = trackRef.current;
    const first = track?.firstElementChild as HTMLElement | null;
    if (!track || !first) return 0;
    const gap = parseFloat(getComputedStyle(track).columnGap || '0') || 0;
    return first.offsetWidth + gap;
  };
  const goTo = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const next = (index + total) % total;
    track.scrollTo({
      left: next * getStep(),
      behavior: 'smooth'
    });
  }, [total]);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const step = getStep();
      if (!step) return;
      const atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 4;
      const index = atEnd ? total - 1 : Math.round(track.scrollLeft / step);
      setActive(Math.min(Math.max(index, 0), total - 1));
    };
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => track.removeEventListener('scroll', onScroll);
  }, [total]);
  return <div className="relative" aria-roledescription="carousel" aria-label="Pages from Volume 2" data-id="element-171" data-mp="3htdrjfnq10xv">
      <div ref={trackRef} className="no-scrollbar -mx-5 flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-px-5 px-5 pb-10 pt-2 lg:mx-0 lg:scroll-px-0 lg:px-0" data-id="element-172" data-mp="3d0kmsrs95sls">
        {previewPages.map((page, index) => <figure key={page.src} className="w-[80%] flex-none snap-start sm:w-[46%] lg:w-[calc((100%-2.5rem)/3)]" aria-roledescription="slide" aria-label={`${index + 1} of ${total}: ${page.title}`} data-id="element-173" data-mp="9du632takmtd" data-mpi={index}>
            <div className="overflow-hidden rounded-[20px] border border-brand-charcoal/8 bg-white p-2 shadow-[0_26px_50px_-30px_rgba(90,70,40,0.5)]" data-id="element-174" data-mp="she4mz210hb5">
              <img src={page.src} alt={`${page.page} — ${page.title}, a real page from Volume 2`} width={780} height={975} loading={index < 2 ? 'eager' : 'lazy'} decoding="async" draggable={false} className="block aspect-[4/5] h-auto w-full select-none rounded-[14px] object-contain" data-id="element-175" data-mp="enwta1jhuaw7" />
            </div>
            <figcaption className="mt-4 px-1" data-id="element-176" data-mp="3vx32pibw2nzc">
              <span className="block font-body text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-gold" data-id="element-177" data-mp="3vzxlz6j622r6">
                {page.page}
              </span>
              <span className="mt-1 block font-display text-[1.25rem] font-bold leading-snug text-brand-charcoal" data-id="element-178" data-mp="1u56ryaxjpi5m">
                {page.title}
              </span>
            </figcaption>
          </figure>)}
      </div>

      <div className="flex items-center justify-center gap-5" data-id="element-179" data-mp="3akudtn3lov2q">
        <ArrowButton label="Previous page" onClick={() => goTo(active - 1)} data-id="element-180" data-mp="3of3s6gi281hz">
          <ChevronLeftIcon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" data-id="element-181" data-mp="3jndcrw49mnjp" />
        </ArrowButton>
        <div className="flex items-center gap-2.5" data-id="element-182" data-mp="26c7pqx7zplr">
          {previewPages.map((page, index) => <button key={page.src} type="button" aria-label={`Show ${page.title}`} aria-current={index === active} onClick={() => goTo(index)} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-cta focus-visible:ring-offset-2 ${index === active ? 'w-8 bg-brand-cta' : 'w-2 bg-brand-charcoal/20 hover:bg-brand-charcoal/35'}`} data-id="element-183" data-mp="3tcde3m68pv12" data-mpi={index} />)}
        </div>
        <ArrowButton label="Next page" onClick={() => goTo(active + 1)} data-id="element-184" data-mp="2efsw2edam3jl">
          <ChevronRightIcon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" data-id="element-185" data-mp="2ei6o0ecoezmx" />
        </ArrowButton>
      </div>
    </div>;
}
function ArrowButton({
  label,
  onClick,
  children
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return <button type="button" onClick={onClick} aria-label={label} className="hidden h-11 w-11 items-center justify-center rounded-full border border-brand-charcoal/10 bg-white text-brand-charcoal shadow-[0_10px_24px_-12px_rgba(42,37,32,0.4)] transition-[background-color,transform] duration-150 ease-out hover:bg-brand-ivory active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-cta md:flex" data-id="element-186" data-mp="1qskplt44oofz">
      {children}
    </button>;
}